/**
 * Created by Ella Ma on 05/25/2019.
 *  Description:
 * ------------------
 * Updated by Daniel on 7/10/2023, added alignment property for all columns
 */
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GridOptions } from 'ag-grid-community';

import { TamDashboardActions } from '../tam-dashboard.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { TamDashboardGridTileBaseConfiguration } from '../tam-dashboard-tile.base.config';
import { GridVisualizationSettings } from '../../../tamalelibs/models/tile.model';
import { TamCustomGridColumnFilterComponent } from '../../../widgets/ag-grid-column-filter/custom-grid-column-filter.component';
import { TamCustomGroupRowInnerRenderer } from '../../../widgets/ag-grid-column-filter/custom-group-row-inner-renderer.component';
import { MultipleFilesRendererComponent } from '../../../widgets/ag-grid/multiple-files-renderer.component';

@Component({
    selector: 'tam-dashboard-grid-tile',
    templateUrl: './tam-dashboard-grid-tile.component.html',
    styleUrls: ['./tam-dashboard-grid-tile.component.scss']
})
export class TamDashboardGridTileComponent implements OnInit, OnDestroy {
    @ViewChild('gridInst', { static: false }) agGrid: ElementRef;
    @ViewChild('topGrid', { static: false }) topGrid;
    @ViewChild('bottomGrid', { static: false }) bottomGrid;

    columnDefs: any = [];
    bottomColumnDefs = [];
    data: any = [];
    bottomData = [];
    displayDataLen = 0;
    gridOptions: GridOptions;
    bottomOptions: GridOptions;
    contextMenuConfig: ContextMenuConfig;
    excelStyles;
    inited = false;
    get isPrintView(): boolean {
        return this.gridOptions.domLayout === 'print';
    }
    name: string;
    gridSettingClass;
    showItemCount: boolean;
    showPrintCustomHeader: boolean;
    showGridContextMenu: boolean;
    readonly: boolean;
    aggFuncs: any;
    frameworkComponents: any;
    groupRowInnerRenderer;
    groupRowRendererParams;

    gridActualWidth;

    private _destroySubscriptions: Array<Subscription> = [];
    private _config: TamDashboardGridTileBaseConfiguration;
    private _gridApi;
    private _gridColumnApi;
    private _bottomGridApi;
    private _bottomGridColumnApi;
    private _gridSetting: GridVisualizationSettings;
    private _matchPrint: MediaQueryList = window.matchMedia('print');

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
    ) { }

    exportAsXLS() {
        this.excelStyles = this._config.getExcelStyles();

        this._config.onExport();
    }

    ngOnInit() {
        // safari not support addEventListener, support addListener
        if (this._matchPrint) {
            if (this._matchPrint.addEventListener) {
                this._matchPrint.addEventListener('change', this._resizeGridColumnSize);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['addListener'](this._resizeGridColumnSize);
            }
        }
    }

    ngOnDestroy() {
        if (this._matchPrint) {
            if (this._matchPrint.removeEventListener) {
                this._matchPrint.removeEventListener('change', this._resizeGridColumnSize);
            } else if (this._matchPrint['addListener']) {
                this._matchPrint['removeListener'](this._resizeGridColumnSize);
            }
            this._resizeGridColumnSize = null;
        }
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        if (this._config) {
            this._config.destroy();
        }
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._gridColumnApi = params.columnApi;
        if (this._config) {
            this._config.onGridReady(this.agGrid, params);
        }
    }

    onBottomGridReady(params) {
        this._bottomGridApi = params.api;
        this._bottomGridColumnApi = params.columnApi;
        this._resetBottomState();
    }

    onRowClicked(params) {
        params.node.expanded = !params.node.expanded;
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.onGroupExpandedOrCollapsed(params.rowIndex);
        } else {
            params.api.onGroupExpandedOrCollapsed(params.rowIndex);
        }
    }

    setConfig(config: TamDashboardGridTileBaseConfiguration): boolean {
        // modify reason, same as pie chart
        this.inited = false;
        this._config = config;
        if (this._config['_widget'] && this._config['_widget'].tile) {
            if (this._config['_widget'].tile.visualization.settings.gridSetting) {
                this._gridSetting = this._config['_widget'].tile.visualization.settings.gridSetting;
            } else {
                this._gridSetting = new GridVisualizationSettings();
            }
        } else {
            this._gridSetting = new GridVisualizationSettings();
        }
        this._initGridSettings();
        this.name = config.name || '';
        this._initGridOption();
        if (!this.contextMenuConfig) {
            this._initContextMenu();
        }
        this._init();
        // home page, refresh tile
        if (this._gridApi && this._gridColumnApi) {
            this._config.onGridReady(this.agGrid, {
                api: this._gridApi,
                columnApi: this._gridColumnApi
            });
        }
        setTimeout(() => {
            this.inited = true;
        });
        return true;
    }

    showContextMenu(e: MouseEvent) {
        if (this._config) {
            this._changeDetectorRef.detectChanges();
            this.contextMenuConfig.show$.next(new ContextMenuShowAction(e, ''));
        }
    }

    onBodyScroll(e) {
        if (this._config) {
            this._config.onBodyScroll(e);
        }
    }

    private _init() {
        this._destroySubscriptions.forEach(element => element.unsubscribe());
        this._destroySubscriptions.push(
            this._config.actionSubject$.pipe(
                filter((action: any) => action && action.id === this._config.id),
            ).subscribe(action => {
                this._onSubscribeActions(action);
            })
        );
    }

    private _initGridSettings() {
        this.showItemCount = this._gridSetting.showItemCount;
        this.showPrintCustomHeader = this._gridSetting.showColumnHeaders;
        this.showGridContextMenu = this.readonly && this.gridOptions.domLayout !== 'print' && this._gridSetting.showGroupingPanel;
        this.gridSettingClass = {
            'hide-row-lines': !this._gridSetting.showGridLines,
            'hide-grid-header': !this._gridSetting.showColumnHeaders,
            'hide-row-background': !this._gridSetting.showRowBackground,
            'hide-grid-border': !this._gridSetting.showTableBorder,
            'hide-group-panel': !this._gridSetting.showGroupingPanel,
            'hide-group-panel-all': !this._gridSetting.showGroupingPanel && !this._gridSetting.showItemCount,
            'hide-grid-top-border': !this._gridSetting.showTableBorder
        };
    }

    private _resizeGridColumnSize = (mql) => {
        if (mql.matches) {
            const syncSizeForGridheaderFunc = () => {
                const gridWidth = this._gridApi.gridCore.getGui().clientWidth;
                const contentWidth = this._config.sumColumnsWidth();
                if (contentWidth > 0) {
                    const actualWidth = Math.max(gridWidth, contentWidth);
                    this.gridActualWidth = Math.min(actualWidth, document.documentElement.clientWidth - 30) + 'px';
                } else {
                    this.gridActualWidth = undefined;
                }
                this._changeDetectorRef.detectChanges();
            };

            // the first sync for the scenario where the printing action is not trigger from server.
            // In that case, the grid size maybe changed by switching into print layout.
            if (!this._config.isPrintingFromServer) {
                syncSizeForGridheaderFunc();
            }

            this._config.resetColumnsToFit();

            // the second sync made for printing from server, in which case the grid size is changed by the function above.
            if (this._config.isPrintingFromServer) {
                syncSizeForGridheaderFunc();
            }

        } else {
            this.gridActualWidth = undefined;
        }
    };

    private _initGridOption() {
        this.gridOptions = {
            debug: false,
            immutableColumns: false,  // grid 21.1.0 new Properties
            alignedGrids: [],
            defaultColDef: {
                filter: 'agSetColumnFilter',
                minWidth: this._config.otherGridOptions['minWidth'],
                maxWidth: this._config.otherGridOptions['maxWidth'],
                sortable: true,
                resizable: this._config.otherGridOptions['resizable'],
                suppressMenu: this._config.otherGridOptions['suppressMenu'],
                suppressMovable: this._config.otherGridOptions['suppressMovable'],
                cellClass: ['font_size10'],
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            autoGroupColumnDef: { minWidth: 200 },
            groupDisplayType: 'groupRows',
            // animateRows: true, // remove animate now, in the futrure update ag-grid
            domLayout: this._config.otherGridOptions['domLayout'],
            suppressCellFocus: true,
            enableBrowserTooltips: true,
            suppressAggFuncInHeader: true, // To turn this off header name after call aggFuns
            // when the grid has horizontal scroll bar, call autoSizeColumns, not all column worked
            suppressColumnVirtualisation: true,
            rowGroupPanelShow: this._config.otherGridOptions['rowGroupPanelShow'],
            rowSelection: 'single',
            sortingOrder: ['asc', 'desc', null],
            maintainColumnOrder: true,
            // suppressMultiSort: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            suppressRowTransform: true,
            getContextMenuItems: this._config.getContextMenuItems(),
            onFilterChanged: this._config.onFilterChanged(),
            onColumnVisible: this._config.onColumnVisible(),
            onColumnMoved: this._config.onColumnMoved(),
            onColumnPinned: this._config.onColumnPinned(),
            onColumnResized: this._config.onColumnResized(),
            onRowGroupOpened: this._config.onRowGroupOpened(),
            onColumnRowGroupChanged: this._config.onColumnRowGroupChanged(),
            onExpandOrCollapseAll: this._config.onExpandOrCollapseAll(),
            getMainMenuItems: this._config.getMainMenuItems(),
            onCellClicked: this._config.onCellClicked(),
        };
        this.bottomOptions = {
            alignedGrids: [],
            defaultColDef: {
                minWidth: this._config.otherGridOptions['minWidth'],
                maxWidth: this._config.otherGridOptions['maxWidth'],
                resizable: this._config.otherGridOptions['resizable']
            },
            suppressCellFocus: true,
            suppressRowGroupHidesColumns: true,
            domLayout: this._config.otherGridOptions['domLayout'],
        };
        this.excelStyles = this._config.getExcelStyles();
        this.readonly = this._config.readonly;
        this.aggFuncs = this._config.setAggFuncs();
        this.gridOptions.alignedGrids.push(this.bottomOptions);
        this.bottomOptions.alignedGrids.push(this.gridOptions);
        this.frameworkComponents = {
            customGridColumnFilter: TamCustomGridColumnFilterComponent,
            groupRowInnerRenderer: TamCustomGroupRowInnerRenderer,
            cellRenderer: MultipleFilesRendererComponent,
        };
        this.groupRowRendererParams = {
            innerRenderer: 'groupRowInnerRenderer',
            innerRendererParams: this._config.groupRowRendererParams
        };
    }

    private _initContextMenu() {
        if (this._config.readonly) {
            this.contextMenuConfig = this._config.initContextMenu();
        }
    }

    private _resetBottomState() {
        if (this._gridColumnApi) {
            this.bottomColumnDefs = this._config.resetBottomState(this.bottomColumnDefs);
            if (this._bottomGridApi) {
                this._bottomGridApi.setColumnDefs(this.bottomColumnDefs);
                this._bottomGridColumnApi.resetColumnState();
            }
        }
    }

    private _onSubscribeActions(action) {
        switch (action.type) {
            case TamDashboardActions.SET_TILE_DATA: {
                this.data = action.payload || [];
                if (this.data && this.data.length > 0) {
                    this._config.postSetData();
                }
                this._changeDetectorRef.detectChanges();
                break;
            }
            case TamDashboardActions.SET_TILE_BOTTOM_GRID_DATA: {
                this._resetBottomState();
                this.bottomData = action.payload || [];
                if (this.bottomData.length === 0) {
                    this._bottomGridApi = null;
                    this._bottomGridColumnApi = null;
                }
                break;
            }
            case TamDashboardActions.GET_TILE_GRID_ALL_DATA: {
                action.subject$.next({
                    type: 'GRID_LAYOUT',
                    payload: { id: this._config.id, data: this.data }
                });
                break;
            }
            case TamDashboardActions.SET_GRID_COLUMN_DEF: {
                this.columnDefs = action.payload;
                // The clonedeep method of lodash cannot be used. It will cause the dashboard page to be on the home page. After switching for several times, the rendering is very slow
                action.payload.forEach(element => {
                    this.bottomColumnDefs.push({ ...element });
                });
                break;
            }

            case TamDashboardActions.RESET_BOTTOM_GRID_COLUMN_WIDTH: {
                this._resetBottomState();
                break;
            }
            case TamDashboardActions.SET_COLUMN_SORT_COMPARATOR:
                this.columnDefs.forEach((column) => {
                    const index = action.payload.findIndex(item => item.colId === column.colId);
                    if (index !== -1) {
                        const rowGroupIndex = action.payload[index].rowGroupIndex;
                        const dataType = column.gridRefData.columnSetting.dataType;
                        column.comparator = (valueA, valueB, nodeA, nodeB) => this._config.customColumnSortComparator(nodeA, nodeB, dataType, rowGroupIndex);
                    } else {
                        delete column.comparator;
                    }
                });
                this._gridApi.setColumnDefs(this.columnDefs);
                break;
            case TamDashboardActions.SET_GRID_TOTAL_ITEMS: {
                this.displayDataLen = action.payload || 0;
                this._changeDetectorRef.detectChanges();
                break;
            }
            case TamDashboardActions.UPDATE_EXPORT_STYLES: {
                this.exportAsXLS();
                break;
            }
            case TamDashboardActions.DISABLED_COLUMN_SORT: {
                this.columnDefs.forEach(column => {
                    column.sortable = false;
                });
                this._gridApi.setColumnDefs(this.columnDefs);
                break;
            }
            case TamDashboardActions.SET_TILE_CONFIG: {
                // old grid tile will not have gridSetting
                if (action.payload.visualization.settings.gridSetting) {
                    this._gridSetting = action.payload.visualization.settings.gridSetting;
                }
                this._initGridSettings();
                break;
            }
            case TamDashboardActions.SET_BOTTOM_GRID_COLUMN_VISIBLE: {
                if (this._bottomGridColumnApi) {
                    this._bottomGridColumnApi.setColumnVisible(action.payload.colId, action.payload.visible);
                    this.bottomColumnDefs.forEach(item => {
                        if (item.colId === action.payload.colId) {
                            item.hide = !action.payload.visible;
                        }
                    });
                }
                break;
            }
            case TamDashboardActions.REDRAW_GRID_ROWS: {
                this._gridApi.redrawRows();
                break;
            }
        }
    }
}
